import { format as d3format } from 'd3';
import { TipTransaction } from './dual-axis-bar-chart.component';
import { compact, compactCount } from './dual-axis-bar-chart.component';

/** One row of get_daily_transaction_summary(). */
export interface DailySummary {
  local_day: string;
  total_volume: number;
  total_reversals: number;
  txn_count: number;
  p25: number | null;
  median: number | null;
  p75: number | null;
  transactions: TipTransaction[] | null;
}

/** Monthly rollup with a numeric x for testing xValuesProperty. */
export interface MonthlySummary {
  label: string;
  epoch: number;
  monthly_volume: number;
  monthly_reversals: number;
  txn_count: number;
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export const currency = (n: number) => (n < 0 ? '-$' : '$') + compact(Math.abs(n));
export const count = compactCount;
export const dollars = (n: number) => d3format('$,.2f')(n);

// ---------------------------------------------------------------------------
// March — real rows. Reversals are synthetic: the query returned 0 for every
// day because refunds were sitting in the sale types as negative amounts.
// ---------------------------------------------------------------------------

type Row = [string, number, number, number, number, number, number];

const MARCH_REAL: Row[] = [
  // label,    volume,      reversals,  count, p25,    median, p75
  ['Mar 01', 343220.02,   4820.15,   688, 100.00, 224.48, 481.03],
  ['Mar 02', 293070.47,       0.00,   538,  97.19, 225.03, 446.93],
  ['Mar 03', 156755.24,   1290.00,   193, 119.11, 275.50, 604.77],
  ['Mar 04', 238699.29,  12440.60,   404,  79.64, 227.45, 417.03],
  ['Mar 05', 669681.42,   3015.75,  1197,  45.00, 112.79, 293.55],
  ['Mar 06', 1983225.18, 48902.33,  5246,  36.00,  65.99, 164.99],
  ['Mar 07', 325145.06,       0.00,   721,  90.25, 214.58, 440.33],
  ['Mar 08', 354292.77,   2255.40,   719,  80.89, 215.00, 449.60],
];

function fromRow([local_day, total_volume, total_reversals, txn_count, p25, median, p75]: Row): DailySummary {
  return { local_day, total_volume, total_reversals, txn_count, p25, median, p75, transactions: null };
}

/** Mar 01–08 from the function, Mar 09–31 generated. */
export const MARCH: DailySummary[] = [
  ...MARCH_REAL.map(fromRow),
  ...Array.from({ length: 23 }, (_, k) => busyDay(`Mar ${String(k + 9).padStart(2, '0')}`)),
];

// ---------------------------------------------------------------------------
// Low-volume days — these carry `transactions` instead of quartiles alone
// ---------------------------------------------------------------------------

export const LOW_VOLUME: DailySummary[] = [
  lowDay('Sep 02', 0,       ['09:54:35', 925.35], ['10:07:40', 265.35]),
  lowDay('Sep 03', 0),
  lowDay('Sep 04', 312.40,  ['08:12:02', 45.00], ['11:41:19', 1290.10], ['15:03:55', 87.25]),
  lowDay('Sep 05', 89.99),
  lowDay('Sep 06', 0,       ['07:55:11', 3410.00]),
  lowDay('Sep 07', 1840.25, ['09:02:44', 120.00], ['09:31:08', 340.75], ['13:18:52', 88.40],
                            ['16:44:01', 1502.99], ['18:22:37', 210.00]),
  lowDay('Sep 08', 75.00,   ['10:15:00', 640.20], ['14:50:33', 75.00]),
  lowDay('Sep 09', 0),
  lowDay('Sep 10', 0,       ['11:11:11', 2200.00], ['17:45:29', 95.50]),
  lowDay('Sep 11', 425.60,  ['08:30:00', 410.00], ['12:00:00', 380.00], ['19:20:15', 1015.60]),
];

/** Mixed month: quartile days, transaction days and empty days interleaved. */
export const MIXED: DailySummary[] = [
  ...MARCH_REAL.slice(0, 4).map(fromRow),
  lowDay('Mar 05', 155.25, ['09:10:00', 820.00], ['13:44:20', 155.25]),
  lowDay('Mar 06', 0),
  ...MARCH_REAL.slice(4, 7).map(fromRow),
  lowDay('Mar 10', 2400.00, ['10:00:00', 99.99], ['11:30:00', 2400.00], ['16:05:45', 310.50]),
];

// ---------------------------------------------------------------------------
// Thirteen months — even spacing plus an epoch for xValuesProperty
// ---------------------------------------------------------------------------

export const MONTHLY: MonthlySummary[] = [
  { label: 'Sep 2025', epoch: Date.UTC(2025, 8), monthly_volume: 5_612_400, monthly_reversals: 142_310, txn_count: 100 },
  { label: 'Oct 2025', epoch: Date.UTC(2025, 9), monthly_volume: 5_048_900, monthly_reversals: 98_440, txn_count: 88 },
  { label: 'Nov 2025', epoch: Date.UTC(2025, 10), monthly_volume: 5_305_150, monthly_reversals: 211_005, txn_count: 82 },
  { label: 'Dec 2025', epoch: Date.UTC(2025, 11), monthly_volume: 5_598_720, monthly_reversals: 388_920, txn_count: 124 },
  { label: 'Jan 2026', epoch: Date.UTC(2026, 0), monthly_volume: 5_480_310, monthly_reversals: 402_115, txn_count: 101 },
  { label: 'Feb 2026', epoch: Date.UTC(2026, 1), monthly_volume: 4_512_660, monthly_reversals: 155_780, txn_count: 79 },
  { label: 'Mar 2026', epoch: Date.UTC(2026, 2), monthly_volume: 5_159_880, monthly_reversals: 174_220, txn_count: 122 },
  { label: 'Apr 2026', epoch: Date.UTC(2026, 3), monthly_volume: 5_092_440, monthly_reversals: 130_660, txn_count: 119 },
  { label: 'May 2026', epoch: Date.UTC(2026, 4), monthly_volume: 5_210_005, monthly_reversals: 96_300, txn_count: 118 },
  { label: 'Jun 2026', epoch: Date.UTC(2026, 5), monthly_volume: 5_367_890, monthly_reversals: 188_450, txn_count: 125 },
  { label: 'Jul 2026', epoch: Date.UTC(2026, 6), monthly_volume: 6_884_200, monthly_reversals: 512_770, txn_count: 139 },
  { label: 'Aug 2026', epoch: Date.UTC(2026, 7), monthly_volume: 5_490_770, monthly_reversals: 167_930, txn_count: 105 },
  { label: 'Sep 2026', epoch: Date.UTC(2026, 8), monthly_volume: 1_002_310, monthly_reversals: 22_400, txn_count: 21 },
];

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function rand(lo: number, hi: number): number {
  return lo + Math.random() * (hi - lo);
}

function r2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** A day with a handful of transactions listed individually. */
function lowDay(local_day: string, reversals: number, ...txns: [string, number][]): DailySummary {
  if (!txns.length) {
    return {
      local_day, total_volume: 0, total_reversals: r2(reversals), txn_count: 0,
      p25: null, median: null, p75: null, transactions: null,
    };
  }
  const amounts = txns.map(t => t[1]).sort((a, b) => a - b);
  return {
    local_day,
    total_volume: r2(amounts.reduce((a, b) => a + b, 0)),
    total_reversals: r2(reversals),
    txn_count: txns.length,
    p25: quantile(amounts, 0.25),
    median: quantile(amounts, 0.5),
    p75: quantile(amounts, 0.75),
    transactions: txns.map(([time, amount], i) => ({
      id: `mock-${local_day.replace(' ', '')}-${i}`,
      amount,
      time: `2026-09-${local_day.slice(-2)}T${time}`,
      tz: 'America/Chicago',
    })),
  };
}

/** A busy day: quartiles only, no list. Roughly a third of days see no returns. */
function busyDay(local_day: string): DailySummary {
  const n = Math.round(rand(190, 1450));
  const median = r2(rand(65, 275));
  const volume = r2(n * rand(280, 900));
  return {
    local_day,
    total_volume: volume,
    total_reversals: Math.random() < 0.35 ? 0 : r2(volume * rand(0.002, 0.04)),
    txn_count: n,
    p25: r2(median * rand(0.35, 0.55)),
    median,
    p75: r2(median * rand(1.8, 2.6)),
    transactions: null,
  };
}

/** Linear interpolation, matching Postgres percentile_cont. */
function quantile(sorted: number[], p: number): number {
  if (!sorted.length) return 0;
  const pos = p * (sorted.length - 1);
  const lo = Math.floor(pos), hi = Math.ceil(pos);
  return r2(sorted[lo] + (pos - lo) * (sorted[hi] - sorted[lo]));
}